import { useCallback, useEffect, useMemo } from 'react'

import { useAuth } from '../../_hooks/use-auth'
import {
  getImportedListingsForUser,
  importListingFromUrl,
} from '../../_data/imported-listings'
import {
  getQuickPassProfileByUserId,
  saveListing,
} from '../../_data/quickpass-profile'
import { normalizeListingUrl } from '../../_utilities/normalize-listing-url'
import {
  SdkAuthStatePayload,
  SdkHostInboundMessage,
  SdkHostIsListingSavedPayload,
  SdkHostResponsePayload,
  SdkHostResponseStatus,
  SdkHostSaveListingPayload,
  SdkListingSavedPayload,
  SdkUserSnapshot,
} from './types'

/**
 * Invisible page used as the cross-site SDK's auth bridge. Hosts mount this
 * route in a hidden iframe via `quickpass/sdk/sdk.js`; the page then
 * broadcasts QuickPass auth state to the parent window via postMessage and
 * accepts request/response commands (sign-out, save-listing) from the host.
 *
 * Wire-format owners: see `./types.ts`. Any change to the message shapes
 * must be mirrored in `quickpass/sdk/sdk.js`.
 *
 * Renders nothing visible — the bridge is purely a postMessage relay.
 *
 * @returns {null}
 */
const SdkBridgePage = (): null => {
  const { quickPassUser, isAuthenticated, isLoading, signOut } = useAuth()

  const userSnapshot = useMemo<SdkUserSnapshot | null>(() => {
    if (quickPassUser === null) {
      return null
    }

    return {
      id: quickPassUser.id,
      email: quickPassUser.email,
      displayName: quickPassUser.displayName,
    }
  }, [quickPassUser])

  const postResponse = useCallback(
    (
      requestId: string,
      status: SdkHostResponseStatus,
      data: unknown,
      errorMessage: string | null,
    ): void => {
      if (window.parent === window) {
        return
      }

      const response: SdkHostResponsePayload = {
        source: 'quickpass',
        type: 'host-response',
        requestId,
        status,
        data,
        errorMessage,
      }

      window.parent.postMessage(response, '*')
    },
    [],
  )

  const broadcastListingSaved = useCallback(
    (listingId: string, url: string | null): void => {
      if (window.parent === window) {
        return
      }

      const payload: SdkListingSavedPayload = {
        source: 'quickpass',
        type: 'listing-saved',
        listingId,
        url,
      }

      window.parent.postMessage(payload, '*')
    },
    [],
  )

  const handleSaveListingRequest = useCallback(
    async (
      requestId: string,
      payload: SdkHostSaveListingPayload,
    ): Promise<void> => {
      if (quickPassUser === null) {
        postResponse(requestId, 'unauthenticated', null, null)
        return
      }

      if (typeof payload.url !== 'string' || payload.url === '') {
        postResponse(
          requestId,
          'error',
          null,
          'saveListing requires a non-empty url.',
        )
        return
      }

      try {
        if (payload.quickCasaListingId !== null) {
          const profile = await saveListing(
            quickPassUser.id,
            payload.quickCasaListingId,
            payload.savedFromHost ?? undefined,
          )
          postResponse(
            requestId,
            'ok',
            { kind: 'quickcasa-listing', profile },
            null,
          )
          broadcastListingSaved(payload.quickCasaListingId, payload.url)
          return
        }

        const importedListing = await importListingFromUrl(
          quickPassUser.id,
          payload.url,
        )
        postResponse(
          requestId,
          'ok',
          { kind: 'imported-listing', listing: importedListing },
          null,
        )
        broadcastListingSaved(
          importedListing.id,
          importedListing.sourceUrl ?? payload.url,
        )
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to save listing.'
        postResponse(requestId, 'error', null, message)
      }
    },
    [broadcastListingSaved, postResponse, quickPassUser],
  )

  const handleGetSavedListingsRequest = useCallback(
    async (requestId: string): Promise<void> => {
      if (quickPassUser === null) {
        postResponse(requestId, 'unauthenticated', null, null)
        return
      }

      try {
        const [profile, importedListings] = await Promise.all([
          getQuickPassProfileByUserId(quickPassUser.id),
          getImportedListingsForUser(quickPassUser.id),
        ])

        const savedListingIds = profile?.savedListingIds ?? []
        const savedUrls = Array.from(
          new Set(
            importedListings
              .map(listing => normalizeListingUrl(listing.sourceUrl))
              .filter(normalized => normalized !== ''),
          ),
        )

        postResponse(requestId, 'ok', { savedListingIds, savedUrls }, null)
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to load saved listings.'
        postResponse(requestId, 'error', null, message)
      }
    },
    [postResponse, quickPassUser],
  )

  const handleIsListingSavedRequest = useCallback(
    async (
      requestId: string,
      payload: SdkHostIsListingSavedPayload,
    ): Promise<void> => {
      if (quickPassUser === null) {
        postResponse(requestId, 'unauthenticated', null, null)
        return
      }

      const hasQuickCasaListingId =
        typeof payload.quickCasaListingId === 'string' &&
        payload.quickCasaListingId !== ''
      const hasUrl = typeof payload.url === 'string' && payload.url !== ''

      if (hasQuickCasaListingId === false && hasUrl === false) {
        postResponse(
          requestId,
          'error',
          null,
          'isSaved requires a url or a quickCasaListingId.',
        )
        return
      }

      try {
        let isSaved = false

        if (hasQuickCasaListingId === true) {
          const profile = await getQuickPassProfileByUserId(quickPassUser.id)
          isSaved =
            profile?.savedListingIds.includes(
              payload.quickCasaListingId as string,
            ) ?? false
        }

        if (isSaved === false && hasUrl === true) {
          const target = normalizeListingUrl(payload.url as string)
          const importedListings = await getImportedListingsForUser(
            quickPassUser.id,
          )
          isSaved = importedListings.some(
            listing => normalizeListingUrl(listing.sourceUrl) === target,
          )
        }

        postResponse(requestId, 'ok', { isSaved }, null)
      } catch (caughtError: unknown) {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to check saved state.'
        postResponse(requestId, 'error', null, message)
      }
    },
    [postResponse, quickPassUser],
  )

  useEffect(() => {
    if (isLoading === true) {
      return
    }

    if (window.parent === window) {
      return
    }

    const payload: SdkAuthStatePayload = {
      source: 'quickpass',
      type: 'auth-state',
      isAuthenticated,
      user: userSnapshot,
    }

    window.parent.postMessage(payload, '*')
  }, [userSnapshot, isAuthenticated, isLoading])

  useEffect(() => {
    const handleHostMessage = (event: MessageEvent): void => {
      if (typeof event.data !== 'object' || event.data === null) {
        return
      }

      const incoming = event.data as Partial<SdkHostInboundMessage>

      if (incoming.source !== 'quickpass-host') {
        return
      }

      if (typeof incoming.requestId !== 'string' || incoming.requestId === '') {
        return
      }

      const requestId = incoming.requestId

      if (incoming.type === 'sign-out') {
        signOut()
          .then(() => {
            postResponse(requestId, 'ok', null, null)
          })
          .catch((error: unknown) => {
            const message =
              error instanceof Error ? error.message : 'Sign-out failed.'
            postResponse(requestId, 'error', null, message)
          })
        return
      }

      if (incoming.type === 'save-listing' && incoming.payload !== undefined) {
        void handleSaveListingRequest(requestId, incoming.payload)
        return
      }

      if (incoming.type === 'get-saved-listings') {
        void handleGetSavedListingsRequest(requestId)
        return
      }

      if (
        incoming.type === 'is-listing-saved' &&
        incoming.payload !== undefined
      ) {
        void handleIsListingSavedRequest(requestId, incoming.payload)
      }
    }

    window.addEventListener('message', handleHostMessage)

    return () => {
      window.removeEventListener('message', handleHostMessage)
    }
  }, [
    signOut,
    handleSaveListingRequest,
    handleGetSavedListingsRequest,
    handleIsListingSavedRequest,
    postResponse,
  ])

  return null
}

export default SdkBridgePage
